import { useState } from 'react';
import TopNavigation from '../../components/feature/TopNavigation';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5mp0ifpl7huldvcilhg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setMessage('Thank you! Your message has been sent successfully.');
        form.reset();
        setTimeout(() => {
          setFormStatus('idle');
          setMessage('');
        }, 5000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setFormStatus('error');
      setMessage('Sorry, something went wrong. Please try again.');
      setTimeout(() => {
        setFormStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 500) {
      e.target.value = value;
    } else {
      e.target.value = value.slice(0, 500);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
      <TopNavigation />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-gray-900 mb-4">Get in Touch</h1>
              <p className="text-xl text-gray-600">
                Have questions about HealthScore? We're here to help.
              </p>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      disabled={formStatus === 'submitting'}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors text-sm disabled:bg-gray-100"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      disabled={formStatus === 'submitting'}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors text-sm disabled:bg-gray-100"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors text-sm disabled:bg-gray-100"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors text-sm disabled:bg-gray-100"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    disabled={formStatus === 'submitting'}
                    onChange={handleTextareaChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors resize-none text-sm disabled:bg-gray-100"
                    placeholder="Tell us more about your inquiry..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
                </div>

                {message && (
                  <div className={`p-4 rounded-lg ${
                    formStatus === 'success' 
                      ? 'bg-green-50 text-green-800 border-2 border-green-200' 
                      : 'bg-red-50 text-red-800 border-2 border-red-200'
                  }`}>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-8 py-4 bg-teal-600 text-white text-lg font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mx-auto mb-4">
                  <i className="ri-mail-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Email</h3>
                <p className="text-sm text-gray-600">support@healthscore.com</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Response Time</h3>
                <p className="text-sm text-gray-600">Within 24 hours</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center bg-pink-100 rounded-lg mx-auto mb-4">
                  <i className="ri-customer-service-line text-2xl text-pink-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Support</h3>
                <p className="text-sm text-gray-600">24/7 Available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <span className="text-xl font-bold">HealthScore</span>
            </div>
            <div className="text-sm text-gray-400 text-center">
              HealthScore provides informational insights only and is not intended to diagnose, treat, or replace professional medical advice.
            </div>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
